import template from 'lodash/template';
import {
  ComponentPropsWithoutRef,
  ComponentType,
  createElement,
  JSX,
} from 'react';
// import * as DOMPurify from 'dompurify';

/**
 * A type to create i18n objects used in component props.
 *
 * ```ts
 *
 * type Props = {
 *   ...
 *   i18n?: I18NInterface<'title' | 'description'>;
 * }
 *
 * // Equivalent to
 * type Props = {
 *  ...
 *  i18n?: {
 *    title?: string;
 *    description?: string;
 *  };
 * };
 *
 * const MyComponent = ({ i18n }: Props) => {
 *   return (
 *     <h1>{i18n?.title ?? 'Default Title'}</h1>
 *     <p>{i18n?.description ?? 'Default Description'}</p>
 *     ...
 *   );
 * }
 * ```
 */
export type I18NInterface<Name extends string, Type = string> = {
  [key in Name]: Type;
};

/**
 * A type to extract the i18n object from a component's props.
 *
 * ```ts
 * Props = {
 *   ...
 *   i18n?: I18NFromComponent<typeof SomeOtherComponent>;
 * }
 * ```
 *
 * You can also use it to compose i18n objects:
 *
 * ```ts
 * type I18N = I18NFromComponent<typeof SomeOtherComponent> & I18NInterface<'title' | 'description'>;
 * ```
 *
 * See {@link I18NInterface} for more details about the usage of i18n objects.
 */
export type I18NFromComponent<Component extends ComponentType<any>> =
  ComponentPropsWithoutRef<Component>['i18n'];

/**
 * Expands given message with given parameters. Uses given fallback if the message is undefined.
 *
 * ```ts
 * const expanded = expand('Hello {{name}}!', 'Hello World!', { name: 'John'});
 * // Returns 'Hello John!'
 *
 * const expanded = expand(undefined, 'Hello World!', { name: 'John'});
 * // Returns 'Hello World!'
 * ```
 *
 * It's intended to be used with i18n objects:
 *
 * ```tsx
 * type Props = {
 *   userName: string;
 *   i18n?: I18NInterface<'welcomeMessage'>;
 * };
 *
 * const MyComponent = ({ userName, i18n }: Props) => {
 *  const message = expand(i18n?.welcomeMessage, 'Hello {{name}}!', { name: userName});
 *
 *   return (
 *     <h1>{message}</h1>
 *   );
 * }
 * ```
 */
export const expandStringWithFallback = (
  message: string | undefined,
  fallback: string,
  params?: Record<string, any>,
): string => {
  return expandString(message ?? fallback, params) ?? fallback;
};

export function expandString(
  message: undefined,
  params?: Record<string, any>,
): undefined;
export function expandString(
  message: string,
  params?: Record<string, any>,
): string;

/**
 * Expands given message with given parameters.
 *
 * ```ts
 * const expanded = expand('Hello {{name}}!', { name: 'John'});
 * // Returns 'Hello John!'
 * ```
 */
export function expandString(
  message: string | undefined,
  params?: Record<string, any>,
): string | undefined {
  if (typeof message === 'undefined') {
    return undefined;
  }

  if (typeof params === 'undefined') {
    return message;
  }

  const compiled = template(message, {
    interpolate: /{{([\s\S]+?)}}/g,
  });

  return compiled(params);
}

/**
 * Parses given html expression to react elements wrapped by given `as`.
 * It's intended to be used with i18n objects. Avoid using it for other purposes.
 * The html expression is sanitized and wrapped in a span element to avoid usage of additional third party libraries.
 *
 * ```tsx
 * type Props = {
 *   userName: string;
 *   i18n?: I18NInterface<'welcomeMessage'>;
 * };
 *
 * const MyComponent = ({ userName, i18n }: Props) => {
 *  const message = expand(i18n?.welcomeMessage, 'Hello {{name}}!', {
 *    name: `<strong>${userName}</strong>`,
 *  });
 *
 *   return (
 *     <h1>{parse(message)}</h1>
 *   );
 *
 *   // Results in `<h1>Hello <span><strong>Jane</strong></span></h1>`.
 * }
 * ```
 *
 * If you need to modify the wrapping element, provide the element's name in the `as` argument.
 *
 * ```tsx
 * type Props = {
 *   userName: string;
 *   i18n?: I18NInterface<'welcomeMessage'>;
 * };
 *
 * const MyComponent = ({ userName, i18n }: Props) => {
 *  const message = expand(i18n?.welcomeMessage, 'Hello {{name}}!', {
 *    name: `<strong>${userName}</strong>`,
 *  });
 *
 *   return (
 *     <h1>{parse(message, 'div')}</h1>
 *   );
 *
 *   // Results in `<h1>Hello <div><strong>Jane</strong></div></h1>`.
 * }
 * ```
 */
export const parse = (
  htmlExpression: string,
  as: keyof JSX.IntrinsicElements = 'span',
) => {
  //TODO: Re-Implement sanitation wich works on server & client components.
  // One possible lib is: https://www.npmjs.com/package/isomorphic-dompurify
  // But there is an issue which needs to be resolved: https://github.com/kkomelin/isomorphic-dompurify/issues/54
  // There is a proposed solution in the GitHub issue thread which can be applied.

  // const sanitized = DOMPurify.sanitize(htmlExpression);
  const sanitized = htmlExpression;

  return createElement(as, {
    dangerouslySetInnerHTML: {
      __html: sanitized,
    },
  });
};
